import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'redux-scaffolding-ts';
import { TrainingLevelDto, TrainingLevelsStore } from '../../stores/configuration/events-n-requests/training-levels-store';
import { nameof } from '../../utils/object';
import { ItemReference } from 'stores/dataStore';
import { isNullOrWhiteSpaces } from 'utils/useful-functions';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useTranslation } from 'react-i18next';

interface TrainingLevelEditorProps {
  value: ItemReference;
  onChange?: (value: ItemReference) => void;
  trainingLevels?: TrainingLevelsStore;
  placeholder?: string;
  clearable?: boolean;
  readOnly?: boolean;
  className?: string;
  nullable?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindTrainingLevelEditor: React.FC<TrainingLevelEditorProps> = ({
  value,
  onChange,
  trainingLevels,
  placeholder,
  clearable = true,
  readOnly = false,
  className = '',
  nullable = false,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

  const loadTrainingLevels = useCallback(
    async (search: string = '') => {
      try {
        setIsLoading(true);
        const result = await trainingLevels.getAllAsync({
          searchQuery: '',
          skip: 0,
          take: 100000,
          orderBy: [{ direction: 'Ascending', field: nameof<TrainingLevelDto>('name'), useProfile: false }],
          filter: isNullOrWhiteSpaces(search) ? [] : [`startswith(tolower(${nameof<TrainingLevelDto>('name')}), '${search.toLowerCase()}')`]
        });

        setOptions(
          result.items.map(item => ({
            value: item.id,
            label: item.name
          }))
        );
      } catch (error) {
        console.error('Error loading locations:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [trainingLevels]
  );

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      loadTrainingLevels();
    }
  }, [loadDataOnOpen, options.length, loadTrainingLevels]);

  useEffect(() => {
    if (!loadDataOnOpen || (loadDataOnOpen && selectedValue)) {
      loadTrainingLevels();
    }
  }, [loadDataOnOpen, selectedValue, loadTrainingLevels]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleChange = (newValue: string | null) => {
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;
    const itemRef = selected ? { id: selected.value, title: selected.label } : null;
    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(training => ({
              id: training.value,
              text: training.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      className="w-full"
      searchable={!isLoading}
      disabled={readOnly}
      onOpenDropDown={onOpenDropDown}
    />
  );
};

export default connect(['trainingLevels', TrainingLevelsStore])(TailwindTrainingLevelEditor);
